// DEV ONLY — dummy canvas tool-call messages for visual testing.
// These are appended to chatMessages in ChatContent when import.meta.env.DEV is true.
// Remove this file (and its import in ChatContent.jsx) before shipping.

const DUMMY_DOC_ID = "doc-dummy-canvas-001"
const DUMMY_DOC_TITLE = "Patient Treatment Summary"

/** @type {import("../../chat-bubble/index").ChatMessage[]} */
export const CANVAS_DUMMY_MESSAGES = [
  // ── Message 1: create_canvas ───────────────────────────────────────────────
  {
    _id: "dummy-canvas-msg-1",
    user: "user",
    text: "Write me a treatment summary for this patient.",
    message: [
      {
        event: "query",
        data: { content: "Write me a treatment summary for this patient." },
      },
    ],
  },
  {
    _id: "dummy-canvas-msg-2",
    user: "agent",
    text: "",
    message: [
      {
        event: "ToolCallStarted",
        data: {
          event: "ToolCallStarted",
          tool_name: "create_canvas",
          tool_call_id: "tc-canvas-001",
          args: JSON.stringify({
            title: DUMMY_DOC_TITLE,
            content: [
              "# Patient Treatment Summary",
              "",
              "**Patient:** John Doe, 54 years, Male",
              "**Date:** 2026-06-02",
              "",
              "## Diagnosis",
              "Type 2 Diabetes Mellitus with hypertension.",
              "",
              "## Treatment Plan",
              "Lifestyle modifications alongside pharmacological intervention.",
              "",
              "## Follow-up",
              "Review in 6 weeks.",
            ].join("\n"),
          }),
        },
      },
      {
        event: "ToolCallCompleted",
        data: {
          event: "ToolCallCompleted",
          tool_name: "create_canvas",
          tool_call_id: "tc-canvas-001",
          tool_result: JSON.stringify({
            document: { _id: DUMMY_DOC_ID, title: DUMMY_DOC_TITLE },
            version: { _id: "ver-dummy-001" },
            diffIds: [],
          }),
        },
      },
      {
        event: "RunContent",
        data: {
          content:
            "I've created a treatment summary document in the canvas. You can review and edit it directly — or ask me to refine any section.",
        },
      },
    ],
  },

  // ── Message 2: edit_document (targeted edits) ──────────────────────────────
  {
    _id: "dummy-canvas-msg-3",
    user: "user",
    text: "Add a medications section and make the diagnosis more specific.",
    message: [
      {
        event: "query",
        data: { content: "Add a medications section and make the diagnosis more specific." },
      },
    ],
  },
  {
    _id: "dummy-canvas-msg-4",
    user: "agent",
    text: "",
    message: [
      {
        event: "ToolCallStarted",
        data: {
          event: "ToolCallStarted",
          tool_name: "edit_document",
          tool_call_id: "tc-canvas-002",
          args: JSON.stringify({
            documentId: DUMMY_DOC_ID,
            edits: [
              {
                id: "edit-001",
                old_string: "Type 2 Diabetes Mellitus with hypertension.",
                new_string:
                  "Type 2 Diabetes Mellitus (HbA1c 8.2%) with Stage 1 hypertension (BP 148/92 mmHg).",
                rationale: "Added lab values and staging for clinical precision.",
              },
              {
                id: "edit-002",
                old_string: "## Follow-up",
                new_string:
                  "## Medications\n- Metformin 500 mg twice daily\n- Amlodipine 5 mg once daily\n- Aspirin 100 mg once daily\n\n## Follow-up",
                rationale: "Inserted medications section before follow-up.",
              },
            ],
          }),
        },
      },
      {
        event: "ToolCallCompleted",
        data: {
          event: "ToolCallCompleted",
          tool_name: "edit_document",
          tool_call_id: "tc-canvas-002",
          tool_result: JSON.stringify({
            document: { _id: DUMMY_DOC_ID, title: DUMMY_DOC_TITLE },
            diffIds: ["edit-001", "edit-002"],
          }),
        },
      },
      {
        event: "RunContent",
        data: {
          content:
            "I've suggested 2 changes — a more specific diagnosis line and a new medications section. Accept or reject each one in the canvas.",
        },
      },
    ],
  },

  // ── Message 3: write_document (full rewrite) ───────────────────────────────
  {
    _id: "dummy-canvas-msg-5",
    user: "user",
    text: "Rewrite the whole summary in a formal clinical note style.",
    message: [
      {
        event: "query",
        data: { content: "Rewrite the whole summary in a formal clinical note style." },
      },
    ],
  },
  {
    _id: "dummy-canvas-msg-6",
    user: "agent",
    text: "",
    message: [
      {
        event: "ToolCallStarted",
        data: {
          event: "ToolCallStarted",
          tool_name: "write_document",
          tool_call_id: "tc-canvas-003",
          args: JSON.stringify({
            documentId: DUMMY_DOC_ID,
            rationale: "Full rewrite in formal SOAP-style clinical note format.",
            content: [
              "# Clinical Note — John Doe",
              "",
              "**Date:** 2026-06-02  **Clinician:** Dr. A. Mensah",
              "",
              "## Subjective",
              "54-year-old male presenting for routine chronic disease review. Reports good medication adherence. No chest pain, dyspnoea, or hypoglycaemic episodes since last visit.",
              "",
              "## Objective",
              "- BP: 148/92 mmHg | HR: 78 bpm | Weight: 89 kg | BMI: 29.4",
              "- HbA1c: 8.2% (previous 8.9%)",
              "- Renal function within normal limits.",
              "",
              "## Assessment",
              "Type 2 Diabetes Mellitus — suboptimal control, improving. Stage 1 Hypertension — partially controlled.",
              "",
              "## Plan",
              "1. Continue Metformin 500 mg BD; consider uptitration at next visit if HbA1c remains >7.5%.",
              "2. Continue Amlodipine 5 mg OD; add ACE inhibitor if BP remains elevated.",
              "3. Dietary counselling referral.",
              "4. Review in 6 weeks with repeat HbA1c and lipid panel.",
            ].join("\n"),
          }),
        },
      },
      {
        event: "ToolCallCompleted",
        data: {
          event: "ToolCallCompleted",
          tool_name: "write_document",
          tool_call_id: "tc-canvas-003",
          tool_result: JSON.stringify({
            document: { _id: DUMMY_DOC_ID, title: DUMMY_DOC_TITLE },
            diffIds: ["rewrite-dummy-001"],
          }),
        },
      },
      {
        event: "RunContent",
        data: {
          content:
            "I've proposed a full SOAP-style rewrite of the document. Review it in the canvas — you can accept to replace the current content or reject to keep the original.",
        },
      },
    ],
  },
]
